import type { IntlKey } from 'locales';
import type { Observable } from 'rxjs';
import type { TypedError } from 'utils/errors/errors';

type Backend = 'Pricing Runs DIY' | 'SP Bridge';
export const TIMEOUT_MESSAGE = (backend?: string) => (backend ? `a ${backend} timeout occurred` : `a timeout occurred`);

export function toTypedError(backend: Backend, err?: HttpError): TypedError {
  return isClientError(err)
    ? { type: 'functional', message: extractHttpErrorMessage(err) }
    : { type: 'technical', subType: isHttpTimeoutError(err) ? 'timeout' : undefined, source: backend };
}
export function extractHttpErrorMessage(err?: HttpClientError, options?: { backend?: string }): string | undefined {
  if (isHttpError(err)) {
    if (err?.payload?.message !== undefined) {
      return err?.payload.message;
    } else if (typeof err?.payload === 'string') {
      return err?.payload;
    } else if (err?.status === 504 && !err?.message) {
      return TIMEOUT_MESSAGE(options?.backend);
    }
  }
  if (isHttpTimeoutError(err) && !!err.timeOutErrorMessage) {
    return err.timeOutErrorMessage;
  }
  return err?.message;
}
export function extractTimeout(err?: HttpClientError): number | undefined {
  if (isHttpTimeoutError(err) && !!err.timeOutErrorMessage) {
    return err.timeout;
  }
}
export type HttpErrorType = 'server' | 'client';
export function extractErrorType(err?: HttpClientError): HttpErrorType {
  return isHttpError(err) && isClientError(err) ? 'client' : 'server';
}
export function isServerError(err?: HttpError) {
  return err !== undefined && err.status >= 500;
}
export function isClientError<T>(err?: HttpError<T>) {
  return err !== undefined && err.status >= 400 && err.status < 500;
}

export type ApiLevel = 'Info' | 'Warning' | 'Error' | 'Critical';
export interface ApiMessage<Code extends string = string> {
  toaster?: boolean;
  message?: string;

  code?: Code;
  level?: ApiLevel;
}

export interface BackendApiMessage extends ApiMessage {
  origin?: string;
}

export function isBackendErrorMessage(error: unknown): error is BackendApiMessage {
  return error !== null && typeof error === 'object' && 'origin' in error && 'message' in error;
}
export function isNewFunctionalMessage(error: unknown): error is NewFunctionalMessage {
  return error !== null && typeof error === 'object' && 'value' in error && 'code' in error;
}
export interface FunctionalMessage<T extends string = string> extends ApiMessage {
  errorDescription?: string;
  errorCode?: T;
}

export interface NewFunctionalMessage<
  T extends string = string,
  V extends unknown = unknown | undefined,
> extends ApiMessage {
  value: V;
  code?: T;
}

export function isFormData(value: unknown): value is FormData {
  return value instanceof FormData;
}

export interface HttpError<T = FunctionalMessage> extends Error {
  name: string;
  timeTaken: number;
  status: number;
  ok: boolean;
  correlationId: string;
  traceId?: string;
  url: string;
  message: string;
  payload?: T;
}

export class HttpTimeoutError extends Error {
  public name = 'HttpTimeoutError';
  constructor(
    public timeout: number,
    public timeOutErrorMessage: IntlKey = 'generic.timeout.message',
    public correlationId: string = '',
  ) {
    super();
  }
}

export type HttpClientError = HttpError | HttpTimeoutError;

export function isHttpError(error: unknown): error is HttpError {
  const httpError = error as HttpError;
  return httpError.name === 'AjaxError' || httpError.name === 'HttpError';
}

export function isHttpTimeoutError(error: unknown): error is HttpTimeoutError {
  return (error as HttpTimeoutError).name === 'HttpTimeoutError' || (error as HttpError).status === 504;
}

export interface HttpSuccessCall<T> {
  payload: T;
  attachmentName?: string;
  timeTaken: number;
  status: number;
  ok: boolean;
}
export interface IHttpBody {}

export interface IHttpHeaders {
  Accept?: string;

  ETag?: string;
  'Cache-Control'?: string;
  'Content-Type'?: string;
  'x-azure-ref'?: string;
  'X-Correlation-ID'?: string;
  'X-RetryCount'?: number;
  Authorization?: Nullable<string>;
  'signalr-connection-id'?: string;
  'polling-queue-id'?: string;
  'timezone-offset'?: string;
  Pragma?: string;
  timestamp?: number;
  'X-Fake-Authentication'?: string;
  'X-Fake-Iam-Permissions'?: string[];

  'front-version'?: string;
}
export type HttpConfigTargetHost = 'SpBridge' | 'SecondaryBridge' | 'AdvisoryBridge';
export interface IHttpConfig<Body = IHttpBody | FormData> {
  body?: Body;
  headers?: IHttpHeaders;
  url: string;
  timeOut?: number;
  timeOutErrorMessage?: IntlKey;
  params?: { [key: string]: any };
  parseErrorPayload?: boolean;
  tryParseErrorPayload?: boolean;
  useTextErrorPayload?: boolean;
  expect404NotFound?: boolean;
  targetHost?: 'SpBridge' | 'SecondaryBridge' | 'AdvisoryBridge';
}

export type HttpCall<T> = HttpSuccessCall<T>;

export interface IHttpClient {
  delete(config: IHttpConfig): Observable<HttpCall<void>>;
  deleteJSON<T>(config: IHttpConfig): Observable<HttpCall<T>>;
  getBlob(config: IHttpConfig): Observable<HttpCall<Blob>>;
  getJSON<T>(config: IHttpConfig): Observable<HttpCall<T>>;
  getRAW(config: IHttpConfig): Observable<HttpCall<string>>;
  patch<T>(config: IHttpConfig): Observable<HttpCall<T>>;
  post(config: IHttpConfig): Observable<HttpCall<void>>;
  head(config: IHttpConfig): Observable<HttpCall<void>>;
  postJSON<T>(config: IHttpConfig): Observable<HttpCall<T>>;
  postRAW(config: IHttpConfig): Observable<HttpCall<string>>;
  postBlob(config: IHttpConfig): Observable<HttpCall<Blob>>;
  put(config: IHttpConfig): Observable<HttpCall<void>>;
  putJSON<T>(config: IHttpConfig): Observable<HttpCall<T>>;
  putRaw(config: IHttpConfig): Observable<HttpCall<string>>;
}
